import { useQuery } from '@apollo/client'

import { GetDeptAndAccountDepartmentsDocument } from '@/graphql/purchase-plus/generated/getDeptAndAccountDepartments.generated'
import useSessionUser from '@/modules/access/hooks/useSessionUser'
import { PURCHASE_PLUS_GRAPHQL_API } from '@/modules/shared/constants'
import { checkNetworkStatus } from '@/modules/shared/utils/checkNetworkStatus'
import { extractEdgesWithType } from '@/modules/shared/utils/extractEdgesWithType'
import { ignoreSingleCharacter } from '@/modules/shared/utils/ignoreSingleCharacter'
import { createPaginationUtils } from '@/modules/shared/utils/paginationUtils'

interface UseGetDeptAndAccountDepartmentsProps {
  organisationId?: number
  searchText?: string
}

export const useGetDeptAndAccountDepartments = ({
  organisationId,
  searchText,
}: UseGetDeptAndAccountDepartmentsProps) => {
  const paginationResultsPerPage = 20
  const { currentOrganisation } = useSessionUser()

  const { data, networkStatus, error, refetch, fetchMore } = useQuery(GetDeptAndAccountDepartmentsDocument, {
    variables: {
      organisationId: Number(organisationId || currentOrganisation?.id),
      first: paginationResultsPerPage,
      filters: {
        searchTextFuzzy: ignoreSingleCharacter(searchText),
      },
    },
    context: {
      uri: PURCHASE_PLUS_GRAPHQL_API,
    },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  })

  const pageInfo = data?.organisation?.selfAndDescendants?.pageInfo
  const loadingStates = checkNetworkStatus(networkStatus)

  return {
    error,
    departments: extractEdgesWithType(data?.organisation?.selfAndDescendants),
    loadingStates,
    refetch,
    pageInfo,
    ...createPaginationUtils({ pageInfo, fetchMore, refetch, paginationResultsPerPage }),
    networkStatus,
  }
}
